interface TokenCacheEntry {
    token: string;
    expiry: number; // UNIX timestamp (seconds)
    userId: string;
}

export class TokenCache {
    private readonly storageKey: string;

    constructor(storageKey: string) {
        this.storageKey = storageKey;
    }

    /**
     * Retrieves and validate a cached token from the cache for the specified user.
     *
     * Attempts to read and parse a token cache entry from storage.
     * Validates the token entry against the provided user ID. Returns the token if it's for the user and not expired.
     *
     * @param {string} userId - The unique identifier of the user whose token should be retrieved.
     * @returns {string | null} The cached token if found and valid; otherwise, `null`.
     */
    public get(userId?: string): string | null {
        let rawTokenCacheValue: string | null = null;
        try {
            rawTokenCacheValue = sessionStorage.getItem(this.storageKey);
        } catch (error) {
            console.warn('Error retrieving value from storage.', error);
        }

        if (!rawTokenCacheValue) {
            console.debug('No cached token found.');
            return null;
        }

        try {
            console.debug('Got value from token cache.');
            const entry: TokenCacheEntry = JSON.parse(rawTokenCacheValue);

            if (this.isValid(entry, userId)) {
                console.info('Retrieved valid token from cache');
                return entry.token;
            }
        } catch (error) {
            // token cache entry is corrupt
            console.warn('Failed to parse token from cache.', error);
        }

        return null;
    }

    /**
     * Caches a token in storage for the given user.
     *
     * @param token - The token to cache.
     * @param userId - User identifier.
     * @param expiry - Token expiry UNIX timestamp (seconds).
     */
    public set(token: string, userId: string, expiry: number): void {
        try {
            const entry: TokenCacheEntry = { token, userId, expiry };
            sessionStorage.setItem(this.storageKey, JSON.stringify(entry));
            console.debug('Cached token entry to session storage.');
        } catch (error) {
            console.warn('Error writing value to storage.', error);
        }
    }

    private isValid(entry: TokenCacheEntry, userId?: string): boolean {
        if (typeof entry.token !== 'string' || typeof entry.expiry !== 'number' || typeof entry.userId !== 'string') {
            console.debug('Cached token entry is malformed.');
            return false;
        }

        // Treat token as expired if it's within 2 minutes of expiry
        const currentUnixTime = Math.floor(Date.now() / 1000);
        const minExpiryTimeWithBuffer = currentUnixTime + 2 * 60;
        if (entry.expiry <= minExpiryTimeWithBuffer) {
            console.debug('Cached token has expired.');
            return false;
        }

        if (userId && entry.userId !== userId) {
            console.debug('Cached token user mismatch.');
            return false;
        }

        console.debug('Cached token is valid.');
        return true;
    }
}
